import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel } from '../utils/formHelpers';

const CityAutocompleteField = ({
   field,
   data,
   change,
   hasError,
   errorMessage,
   cityOptions,
   isCitiesLoading,
   tripCountry = '',
   tripCountries,
}) => {

    const options = cityOptions || [];
    const resolvedCountries = (tripCountries && tripCountries.length)
        ? tripCountries
        : (String(tripCountry || '').trim() ? [String(tripCountry).trim()] : []);
    const hasCountry = resolvedCountries.length > 0;
    const countriesLabel = resolvedCountries.join(', ');

    // Default true preserves original behavior for fields that never set
    // `multiple` at all (e.g. packages.city). Only an explicit `false` opts
    // a field into single-select.
    const isMultiple = field.multiple !== false;

    const rawValue = data[field.key];

    // Normalize stored data into a flat array of city name strings,
    // regardless of mode, so lookups below are uniform.
    const selectedCityNames = isMultiple
        ? (Array.isArray(rawValue)
            ? rawValue
            : (typeof rawValue === 'string' && rawValue.trim()
                ? rawValue.split(',').map((c) => c.trim()).filter(Boolean)
                : []))
        : (typeof rawValue === 'string' && rawValue.trim()
            ? [rawValue.trim()]
            : (Array.isArray(rawValue) && rawValue.length ? [rawValue[0]] : []));

    // De-duped resolution: a Map keyed by city name guarantees no duplicate
    // chips even if `options` itself has repeated entries or stale data
    // produced repeated names.
    const resolvedMap = new Map();
    selectedCityNames.forEach((cityName) => {
        if (resolvedMap.has(cityName)) return;
        const matched = options.find((option) => option.city === cityName);
        resolvedMap.set(cityName, matched || {
            country: 'Other',
            state: 'Other',
            city: cityName,
            _manual: true,
        });
    });
    const selectedValues = Array.from(resolvedMap.values());

    const handleChange = (_, newValue) => {
        if (isMultiple) {
            const nextCities = (newValue || []).map((item) => item.city).filter(Boolean);
            change(field.key, Array.from(new Set(nextCities))); // de-dupe on write too
            return;
        }
        // single-select: Autocomplete gives a single object (or null), not an array
        change(field.key, newValue?.city || '');
    };

    return (
        <Autocomplete
            size="small"
            options={options}
            loading={Boolean(isCitiesLoading)}
            multiple={isMultiple}
            value={isMultiple ? selectedValues : (selectedValues[0] || null)}
            groupBy={(option) => `${option.country} / ${option.state || 'Other'}`}
            getOptionLabel={(option) => option?.city || ''}
            isOptionEqualToValue={(option, value) =>
                option.city === value.city
                && option.country === value.country
                && (option.state || 'Other') === (value.state || 'Other')
            }
            onChange={handleChange}
            renderTags={isMultiple ? (value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={`${option.country}-${option.state}-${option.city}-${index}`}
                            label={option.city}
                            size="small"
                            sx={{ borderRadius: '8px' }}
                        />
                    ))
                : undefined}
            renderOption={(props, option) => (
                <li {...props} key={`${option.country}-${option.state}-${option.city}`}>
                    {option.city}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={<FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />}
                    placeholder={
                        !hasCountry
                            ? 'Select a destination first'
                            : (isCitiesLoading ? 'Loading cities...' : (options.length ? (isMultiple ? 'Select city/cities' : 'Select city') : 'No cities found'))
                    }
                    error={hasError}
                    helperText={
                        hasError
                            ? (errorMessage || 'This field is required')
                            : (!hasCountry
                                ? 'Set the destination country on the Destination step first'
                                : (isCitiesLoading
                                    ? `Loading cities for ${countriesLabel}...`
                                    : (options.length ? '' : `Could not load cities for ${countriesLabel}. Check your connection and try again.`)))
                    }
                />
            )}
        />
    );
};

export default CityAutocompleteField;